import { backboneService } from '@/services/backbone.js';
import { assetsClient } from '@/clients/assets-client.js';
import { toastService } from '@/services/toast.js';
import { HatElement } from '@/components/hat-element.js';

class HatLocationCard extends HatElement {
  private _data: any = null;
  private _closeMenusHandler: (() => void) | null = null;
  private _docClickHandler: (() => void) | null = null;

  constructor() {
    super();

    this.root.innerHTML = `
      <style>
        .container {
          margin-bottom: 8px;
        }
        .card {
          display: flex;
          align-items: center;
          gap: 16px;
          background: whitesmoke;
          padding: 14px;
          border-radius: 12px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.08);
          transition: transform 0.1s ease;
        }

        .card {
          cursor: pointer;
        }

        .card:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.14);
        }

        .content {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .name {
          font-weight: 600;
          font-size: 16px;
        }

        .address {
          font-size: 13px;
          color: #555;
        }

        .thumbnail-wrapper {
          width: 80px;
          height: 80px;
          flex-shrink: 0;
        }

        .menu {
          position: relative;
        }

        .menu button {
          background: none;
          border: none;
          font-size: 18px;
          cursor: pointer;
        }

        .dropdown {
          position: absolute;
          right: 0;
          top: 24px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          display: none;
          flex-direction: column;
          min-width: 120px;
          overflow: hidden;
          z-index: 10;
        }

        .dropdown button {
          padding: 8px 12px;
          border: none;
          background: none;
          text-align: left;
          cursor: pointer;
        }

        .dropdown button:hover {
          background: #f2f2f2;
        }

        .menu.open .dropdown {
          display: flex;
        }
      </style>

      <div class="container">
      <div class="card">
        <div class="thumbnail-wrapper">
        </div>
        
        <div class="content">
          <div class="name"></div>
          <div class="address"></div>
        </div>

        <div class="menu" hat-entitlement="feature.locations.edit feature.locations.delete">
          <button>⋮</button>
          <div class="dropdown">
            <button data-action="edit" hat-entitlement="feature.locations.edit">Edit</button>
            <button data-action="delete" hat-entitlement="feature.locations.delete">Delete</button>
          </div>
        </div>
      </div>
      </div>
    `;
  }

  setData(val: any) {
    this._data = val;
    this.render();
  }

  render() {
    if (!this._data) return;

    const { id, name, address, displayImage } = this._data;

    const nameEl = this.root.querySelector<HTMLElement>('.name')!;
    const addressEl = this.root.querySelector<HTMLElement>('.address')!;
    const thumbEl = this.root.querySelector<HTMLElement>('.thumbnail-wrapper')!;

    const addressText = address && Object.keys(address).length
      ? this.formatAddress(address)
      : '';

    nameEl.textContent = name || addressText || 'Name not set';
    addressEl.textContent = name ? addressText : '';
    addressEl.style.display = name ? '' : 'none';

    if (displayImage?.thumbnailDocId) {
      thumbEl.innerHTML = `<hat-image type="thumbnail" document-id="${displayImage.thumbnailDocId}"></hat-image>`;
    } else {
      thumbEl.innerHTML = `<div style="width:80px;height:80px;background:#ddd;border-radius:8px;"></div>`;      
    }

    const card = this.root.querySelector<HTMLElement>('.card')!;
    card.onclick = () => backboneService.navigateTo(`/app/locations/${id}`);

    this.setupMenu();
  }

  formatAddress(address: any) {
    // Adjust this depending on your address schema
    return [
      address.street1,
      address.street2,
      address.city,
      address.state,
      address.postalCode
    ]
      .filter(Boolean)
      .join('\n');
  }

  async setupMenu() {
    const menu = this.root.querySelector('.menu')!;
    const button = menu.querySelector('button')!;
    const dropdown = menu.querySelector('.dropdown')!;

    button.onclick = (e) => {
      e.stopPropagation();
      const isOpen = menu.classList.contains('open');
      document.dispatchEvent(new CustomEvent('hat-close-menus'));
      if (!isOpen) menu.classList.add('open');
    };

    dropdown.querySelector<HTMLElement>('[data-action="edit"]')!.onclick = (e) => {
      e.stopPropagation();
      menu.classList.remove('open');
      backboneService.navigateTo(`/app/locations/${this._data.id}/edit`);
    };

    dropdown.querySelector<HTMLElement>('[data-action="delete"]')!.onclick = async(e) => {
      e.stopPropagation();
      menu.classList.remove('open');
      try {
        await assetsClient.deleteLocation(this._data.id);
        this.dispatchEvent(new CustomEvent('delete', {
          detail: this._data,
          bubbles: true,
          composed: true
        }));
      } catch (error) {
        toastService.show('Failed to delete location. Please try again.');
      }
    };

  }

  connectedCallback() {
    super.connectedCallback();
    const menu = this.root.querySelector<HTMLElement>('.menu')!;
    this._closeMenusHandler = () => menu.classList.remove('open');
    this._docClickHandler   = () => menu.classList.remove('open');
    document.addEventListener('hat-close-menus', this._closeMenusHandler);
    document.addEventListener('click', this._docClickHandler);
    this.render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._closeMenusHandler) document.removeEventListener('hat-close-menus', this._closeMenusHandler);
    if (this._docClickHandler)   document.removeEventListener('click', this._docClickHandler);
  }
}

customElements.define('hat-location-card', HatLocationCard);